import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Section from "../components/Section";
import ProductCard from "../components/ProductCard";
import OrderModal from "../components/OrderModal";
import { getProductImageUrl, supabase } from "../lib/supabaseClient";

const getProductSlug = (product) => {
  const base = product?.product_display_name || product?.name || "";
  const slug = base
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || String(product?.id || "");
};

export default function Shop() {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selected, setSelected] = useState(null);
  const panelRef = useRef(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cartReady, setCartReady] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState("");
  const navigate = useNavigate();
  const { productSlug } = useParams();
  const [statusForm, setStatusForm] = useState({
    order_number: "",
    customer_name: "",
    customer_email: "",
    customer_phone: ""
  });
  const [statusResult, setStatusResult] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      if (!supabaseUrl || !supabaseKey) {
        setError("Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const { data: productsData, error: fetchError } = await supabase
          .from("products")
          .select("*")
          .eq("active", true)
          .order("display_order", { ascending: true })
          .order("name", { ascending: true });
        if (fetchError) throw fetchError;

        const products = productsData || [];

        const imageCache = new Map();
        const resolveImage = async (path) => {
          if (!path) return null;
          if (imageCache.has(path)) return imageCache.get(path);
          const url = await getProductImageUrl(path);
          if (url) imageCache.set(path, url);
          return url || null;
        };

        let variantsData = [];
        if (products.length) {
          const { data, error: variantsError } = await supabase
            .from("product_variants")
            .select("id, product_id, variant_sku, label, color, size, image_path, display_order, active")
            .in(
              "product_id",
              products.map((p) => p.id)
            )
            .eq("active", true)
            .order("display_order", { ascending: true })
            .order("label", { ascending: true });
          if (variantsError) {
            // eslint-disable-next-line no-console
            console.warn("Failed to load product variants", variantsError?.message || variantsError);
          } else {
            variantsData = data || [];
          }
        }

        const variantsByProduct = new Map();
        if (variantsData.length) {
          const variantItems = await Promise.all(
            variantsData.map(async (variant) => {
              const labelValue = variant.color || variant.size || variant.label || "";
              const imageUrl = await resolveImage(variant.image_path);
              return {
                ...variant,
                label_value: labelValue,
                image_url: imageUrl
              };
            })
          );
          variantItems.forEach((variant) => {
            const list = variantsByProduct.get(variant.product_id) || [];
            list.push(variant);
            variantsByProduct.set(variant.product_id, list);
          });
        }

        // attach images (image_path, image_path2, image_path3) + variants
        const withImages = await Promise.all(
          products.map(async (product) => {
            const paths = [product.image_path, product.image_path2, product.image_path3].filter(Boolean);
            const images = [];
            for (const p of paths) {
              const url = await resolveImage(p);
              if (url) images.push(url);
            }
            const displayImage = images[0] || null;
            const variants = variantsByProduct.get(product.id) || [];
            return { ...product, image_url: displayImage, images, variants };
          })
        );

        setProducts(withImages);
      } catch (err) {
        setError(err?.message || "Failed to load products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [supabaseKey, supabaseUrl]);

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [products]
  );

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minPrice ? Number(minPrice) : null;
    const max = maxPrice ? Number(maxPrice) : null;
    return sortedProducts.filter((p) => {
      const nameMatch =
        p.name?.toLowerCase().includes(q) ||
        p.product_type?.toLowerCase().includes(q) ||
        p.product_sub_type?.toLowerCase().includes(q);
      const price = p.selling_cost ?? p.product_cost ?? 0;
      const aboveMin = min === null || price >= min;
      const belowMax = max === null || price <= max;
      return nameMatch && aboveMin && belowMax;
    });
  }, [sortedProducts, search, minPrice, maxPrice]);

  const groupedProducts = useMemo(() => {
    const grouped = new Map();
    filteredProducts.forEach((product) => {
      const key = (product.product_sub_type || "Other").trim() || "Other";
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(product);
    });
    const categories = Array.from(grouped.keys()).sort((a, b) => a.localeCompare(b));
    return categories.map((category) => ({
      category,
      products: grouped.get(category) || []
    }));
  }, [filteredProducts]);

  const productBySlug = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const slug = getProductSlug(product);
      if (slug && !map.has(slug)) {
        map.set(slug, product);
      }
    });
    return map;
  }, [products]);

  useEffect(() => {
    const normalizedSlug = productSlug?.toLowerCase() || "";
    if (!normalizedSlug) {
      setSelected(null);
      return;
    }
    if (!products.length) return;
    const match = productBySlug.get(normalizedSlug);
    setSelected(match || null);
  }, [productSlug, productBySlug, products.length]);

  useEffect(() => {
    if (groupedProducts.length > 0) {
      setExpandedCategory((prev) => prev || groupedProducts[0].category);
    } else {
      setExpandedCategory("");
    }
  }, [groupedProducts]);

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    [cart]
  );

  useEffect(() => {
    const saved = localStorage.getItem("ss_cart");
    if (saved) {
      try {
        setCart(JSON.parse(saved));
      } catch (e) {
        // ignore parse errors
      }
    }
    setCartReady(true);
  }, []);

  useEffect(() => {
    if (cartReady) {
      localStorage.setItem("ss_cart", JSON.stringify(cart));
    }
  }, [cart, cartReady]);

  useEffect(() => {
    if (selected && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selected]);

  const handleSelect = (product) => {
    setSelected(product);
    const slug = getProductSlug(product);
    if (slug) {
      navigate(`/shop/${slug}`);
    } else {
      navigate("/shop");
    }
  };

  return (
    <div className="space-y-6">
      <Section
        eyebrow="Shop"
        title="Official merchandise & music specials"
        description="Order directly from Sasmita & Sangram. Each purchase supports the artists and the music you love."
      >
        <div className="mb-4 rounded-2xl border border-amber-300/40 bg-amber-300/10 text-amber-100 px-4 py-3 text-sm font-semibold text-center">
          All orders ship via our dropshipping partner. Estimated delivery: 10–12 days. International delivery may take up to 20 days.
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-4 grid gap-3 md:grid-cols-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or type"
            className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-3 text-white focus:border-amber-300 outline-none"
          />
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder="Min price"
              className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-3 text-white focus:border-amber-300 outline-none"
            />
            <input
              type="number"
              min="0"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Max price"
              className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-3 text-white focus:border-amber-300 outline-none"
            />
          </div>
          <button
            onClick={() => {
              setSearch("");
              setMinPrice("");
              setMaxPrice("");
            }}
            className="rounded-full bg-amber-400 text-black font-semibold px-4 py-3 shadow-lg"
          >
            Clear filters
          </button>
        </div>
        {loading ? (
          <div className="text-center text-gray-300 py-10">Loading products...</div>
        ) : error ? (
          <div className="text-center text-red-400 py-10">{error}</div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center text-gray-300 py-10">No products available right now.</div>
        ) : (
          <div className="space-y-4">
            {groupedProducts.map(({ category, products }) => {
              const isOpen = expandedCategory === category;
              return (
                <div key={category} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedCategory(isOpen ? "" : category)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left text-white font-semibold"
                  >
                    <span>{category}</span>
                    <span className="text-amber-300 text-sm">{isOpen ? "Hide" : "Show"}</span>
                  </button>
                  {isOpen ? (
                    <div className="px-4 pb-4 pt-1 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                      {products.map((product) => (
                        <ProductCard key={product.id} product={product} onSelect={handleSelect} />
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {selected ? (
        <div ref={panelRef}>
          <OrderModal
            product={selected}
            onClose={() => {
              setSelected(null);
              if (productSlug) navigate("/shop");
            }}
            inline
            onAddToCart={(item) => {
              setCart((prev) => [...prev, item]);
              setSelected(null);
              setShowCart(true);
              if (productSlug) navigate("/shop");
            }}
          />
        </div>
      ) : null}
      <CartButton count={cart.length} onClick={() => setShowCart((p) => !p)} />
      <CartDrawer
        open={showCart}
        onClose={() => setShowCart(false)}
        cart={cart}
        onRemove={(idx) => setCart((prev) => prev.filter((_, i) => i !== idx))}
        total={cartTotal}
        onCheckout={() => {
          localStorage.setItem("ss_cart", JSON.stringify(cart));
          setShowCart(false);
          navigate("/checkout");
        }}
      />
      <OrderStatusChecker
        form={statusForm}
        setForm={setStatusForm}
        result={statusResult}
        loading={statusLoading}
        error={statusError}
        onSearch={async () => {
          const hasOrder = statusForm.order_number.trim();
          const hasCombo =
            statusForm.customer_name.trim() &&
            statusForm.customer_email.trim() &&
            statusForm.customer_phone.trim();
          const hasNamePhone = statusForm.customer_name.trim() && statusForm.customer_phone.trim();
          if (!hasOrder && !hasCombo && !hasNamePhone) {
            setStatusError("Enter an order number, or name + phone, or name + email + phone.");
            setStatusResult([]);
            return;
          }
          setStatusError("");
          setStatusLoading(true);
          try {
            let query = supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(5);
            if (hasOrder) {
              query = query.eq("order_number", statusForm.order_number.trim());
            } else if (hasCombo) {
              query = query
                .eq("customer_name", statusForm.customer_name.trim())
                .eq("customer_email", statusForm.customer_email.trim())
                .eq("customer_phone", statusForm.customer_phone.trim());
            } else if (hasNamePhone) {
              query = query
                .eq("customer_name", statusForm.customer_name.trim())
                .eq("customer_phone", statusForm.customer_phone.trim());
            }
            const { data, error } = await query;
            if (error) throw error;
            setStatusResult(data || []);
          } catch (err) {
            setStatusError(err?.message || "Failed to fetch order status.");
            setStatusResult([]);
          } finally {
            setStatusLoading(false);
          }
        }}
      />
      <ShoppingNote />
    </div>
  );
}

function CartButton({ count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="fixed right-4 bottom-5 md:bottom-auto md:top-24 z-30 inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-semibold px-4 py-3 shadow-lg"
    >
      Cart
      <span className="rounded-full bg-black text-white text-xs font-bold px-2 py-0.5">{count}</span>
    </button>
  );
}

function CartDrawer({
  open,
  onClose,
  cart,
  onRemove,
  total,
  onCheckout
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg h-full bg-[#0a0b12] border-l border-white/10 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80">Cart</p>
            <h4 className="text-lg font-bold text-white">Review & Checkout</h4>
          </div>
          <button onClick={onClose} className="text-sm text-gray-200 hover:text-white">
            Close
          </button>
        </div>
        <div className="p-4 space-y-4">
          {cart.length === 0 ? (
            <div className="text-gray-300">Your cart is empty.</div>
          ) : (
            <>
              <div className="space-y-3">
                {cart.map((item, idx) => (
                  <div
                    key={`${item.product.id}-base-${idx}`}
                    className="rounded-2xl border border-white/10 bg-white/5 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="h-14 w-14 rounded-xl overflow-hidden bg-black/30 border border-white/10 flex-shrink-0 self-start sm:self-auto">
                      {item.variant?.image_url || item.product.image_url ? (
                        <img
                          src={item.variant?.image_url || item.product.image_url}
                          alt={item.product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-semibold">{item.product.name}</div>
                      <div className="text-xs text-gray-300">{item.product.product_type || ""}</div>
                      {(item.variant_label || item.variant?.label_value || item.variant?.color || item.variant?.size || item.variant?.label) ? (
                        <div className="text-xs text-amber-200">
                          Variant: {item.variant_label || item.variant?.label_value || item.variant?.color || item.variant?.size || item.variant?.label}
                        </div>
                      ) : null}
                      <div className="text-xs text-gray-400">Qty: {item.quantity}</div>
                    </div>
                    <div className="text-amber-300 font-semibold sm:self-start">₹{item.unitPrice * item.quantity}</div>
                    <button
                      onClick={() => onRemove(idx)}
                      className="text-xs text-gray-300 hover:text-amber-200"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white">
                <span className="font-semibold">Total</span>
                <span className="text-amber-300 font-bold text-lg">₹{total}</span>
              </div>
            </>
          )}

          {cart.length > 0 ? (
            <button
              onClick={onCheckout}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 text-black font-semibold px-4 py-3 shadow-lg"
            >
              Go to checkout
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ShoppingNote() {
  return (
    <Section>
      <div className="max-w-5xl mx-auto rounded-3xl border border-white/10 bg-white/5 px-6 py-5 text-sm text-gray-200 space-y-2">
        <p className="text-white font-semibold">Shopping experience</p>
        <p>
          This shopping experience is powered and managed by Yuktra AI Solutions, using AI-enabled automation for order processing, payments, and fulfillment coordination.
        </p>
        <p>
          Sasmita Sangram focuses on creating meaningful products for music lovers, while Yuktra AI ensures a smooth, secure, and efficient shopping journey.
        </p>
      </div>
    </Section>
  );
}

function StatusBadge({ label, value }) {
  const colors = {
    paid: "bg-green-500/20 text-green-200 border-green-400/40",
    pending: "bg-amber-500/10 text-amber-200 border-amber-300/40",
    failed: "bg-red-500/10 text-red-200 border-red-400/40",
    completed: "bg-green-500/10 text-green-200 border-green-300/40"
  };
  const cls = colors[(value || "").toLowerCase()] || "bg-white/5 text-gray-200 border-white/10";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${cls}`}>
      {label}: {value || "N/A"}
    </span>
  );
}

function OrderStatusChecker({ form, setForm, result, loading, error, onSearch }) {
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Section
      eyebrow="Order Status"
      title="Track your order"
      description="Check your order by number, or with name + email + phone."
    >
      <div className="grid md:grid-cols-2 gap-4">
        <input
          name="order_number"
          value={form.order_number}
          onChange={handleChange}
          placeholder="Order number (e.g., SS-20250101-1234)"
          className="w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3 text-white focus:border-amber-300 outline-none"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            name="customer_name"
            value={form.customer_name}
            onChange={handleChange}
            placeholder="Name"
            className="w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3 text-white focus:border-amber-300 outline-none"
          />
          <input
            name="customer_email"
            value={form.customer_email}
            onChange={handleChange}
            placeholder="Email"
            className="w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3 text-white focus:border-amber-300 outline-none"
          />
          <input
            name="customer_phone"
            value={form.customer_phone}
            onChange={handleChange}
            placeholder="Phone"
            className="w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3 text-white focus:border-amber-300 outline-none"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 items-center">
        <button
          onClick={onSearch}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-semibold px-5 py-3 shadow-lg disabled:opacity-60"
        >
          {loading ? "Checking..." : "Show status"}
        </button>
        {error ? <span className="text-sm text-red-400">{error}</span> : null}
      </div>

      {result.length ? (
        <div className="mt-5 grid gap-3">
          {result.map((order) => (
            <div key={order.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-white font-semibold">Order {order.order_number}</div>
                  <div className="text-xs text-gray-400">
                    Placed: {formatDateIST(order.created_at, true)}
                  </div>
                  {order.expected_delivery_date ? (
                    <div className="text-xs text-gray-400">
                      Expected delivery: {formatDateIST(order.expected_delivery_date)}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge label="Payment" value={order.payment_status} />
                  <StatusBadge label="Delivery" value={order.delivery_status || order.status} />
                </div>
              </div>
              <div className="text-xs text-gray-400 mt-2 space-y-1">
                <div>Items: {order.notes || order.product_name}</div>
                <div>Qty: {order.quantity} · Total: ₹{order.total_amount_inr}</div>
                {order.tracking_link ? (
                  <div className="pt-2">
                    <a
                      href={order.tracking_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-semibold px-4 py-2 shadow-lg"
                    >
                      Track your product
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

function formatDateIST(dateInput, includeTime = false) {
  if (!dateInput) return "N/A";
  try {
    const date = new Date(dateInput);
    const baseOpts = { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" };
    const opts = includeTime
      ? { ...baseOpts, hour: "2-digit", minute: "2-digit" }
      : baseOpts;
    return new Intl.DateTimeFormat("en-IN", opts).format(date);
  } catch (e) {
    return String(dateInput);
  }
}
